import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { hideMenu } from '../utils';
import { useLoading } from '../contexts/LoadingContext';
import { useLanguage } from '../LanguageContext';
import { useTranslate } from '../services/translation';
import { getBlogBySlug, PortfolioBlog, getTranslationByKey, TranslationContent } from '../api/API';
import BlogDetailSkeleton from '../components/BlogDetailSkeleton';
import '../assets/css/BlogDetail.css';

interface BlogContent {
  html: string;
  style: string;
}

const extractStyleFromHtml = (html: string): BlogContent => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const styleTag = doc.querySelector('style');
  const style = styleTag?.textContent || '';

  if (styleTag) {
    styleTag.remove();
  }

  return {
    html: doc.body.innerHTML,
    style
  };
};

const BlogDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { translate } = useTranslate();
  const { startLoading, stopLoading } = useLoading();
  const [blog, setBlog] = useState<PortfolioBlog | null>(null);
  const [contentData, setContentData] = useState<TranslationContent[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [blogStyle, setBlogStyle] = useState<string>('');
  const [cleanHtml, setCleanHtml] = useState<string>('');

  useEffect(() => {
    hideMenu();
    if (!slug) return;

    const loadBlogDetail = async () => {
      setIsLoading(true);
      startLoading();

      try {
        const blogResponse = await getBlogBySlug(slug);
        if (blogResponse && blogResponse.data) {
          setBlog(blogResponse.data);

          // Récupérer le contenu traduit
          const contentResponse = await getTranslationByKey(blogResponse.data.content);
          if (contentResponse && contentResponse.data) {
            setContentData(contentResponse.data.contents);
          }

          setError(false);
        } else {
          setError(true);
        }
      } catch (err) {
        console.error('Error loading blog:', err);
        setError(true);
      } finally {
        setIsLoading(false);
        stopLoading();
      }
    };

    loadBlogDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    if (contentData) {
      const content = contentData.find((c) => c.lang === language);
      if (content) {
        const { html, style } = extractStyleFromHtml(content.value);
        setBlogStyle(style);
        setCleanHtml(html);
      }
    }
  }, [contentData, language]);

  if (isLoading) {
    return <BlogDetailSkeleton />;
  }

  if (error || !blog) {
    return (
      <div className="blog-detail-error">
        <h1 className="white">{translate('common.blog')}</h1>
        <p className="light">{translate('common.no_blog_found')}</p>
        <button
          className="btn-back"
          onClick={() => navigate(`/${language}/blog`)}
        >
          ← {translate('common.view_more_blogs')}
        </button>
      </div>
    );
  }

  return (
    <div className="blog-detail-page width-100p">
      <Helmet>
        <meta charSet="utf-8" name="description" content={translate(blog.description as any)} />
        <title>{translate(blog.title as any)} – Gence Lucien</title>
        <link rel="canonical" href={`https://gence.bemanjary.com/${language}/blog/${blog.slug}`} />
        <meta property="og:title" content={translate(blog.title as any)} />
        <meta property="og:description" content={translate(blog.description as any)} />
        <meta property="og:type" content="article" />
        {blogStyle && <style>{blogStyle}</style>}
      </Helmet>

      <div className="blog-detail-container">
        <button
          className="btn-back-link"
          onClick={() => navigate(`/${language}/blog`)}
        >
          ← {translate('common.view_more_blogs')}
        </button>

        <article className="blog-detail-article">
          {contentData && (
            <div className="blog-detail-content light">
              {cleanHtml ? (
                <div dangerouslySetInnerHTML={{ __html: cleanHtml }} />
              ) : (
                <p>{translate('common.content_not_available')}</p>
              )}
            </div>
          )}
        </article>
      </div>
    </div>
  );
};

export default BlogDetail;